import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import Domain from "../../services/domain";
import Toolbar from "../controls/Toolbar";
import PubSub from "../../services/PubSub";
import Product from "../../services/product";
import Deployment from "../../services/deployment";
import { useTranslation } from "react-i18next";
import { getCrudPermissions, useHasPermission } from "../../common/Hooks";
import Toggle from "../controls/Toggle";
import { Button } from "react-bootstrap";
import SettingsModal from "../settings/SettingsModal";
import ChangeCnameModal from "./ChangeCnameModal";
import ACTION_TYPES from "../../config/actionTypes";
import TextBox from "../controls/form/TextBox";
import Utils from "../../utils/utils";

const RootDomainDetails = ({ id }) => {
    const [translate] = useTranslation();
    const [domain, setDomain] = useState({});
    const [products, setProducts] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [selectedDeployment, setSelectedDeployment] = useState(null);
    const [assignedProducts, setAssignedProducts] = useState([]);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showChangeCname, setShowChangeCname] = useState(false);
    const [assignedDeploymentsToProduct, setAssignedDeploymentsToProduct] = useState({});
    const [deploymentParams, setDeploymentParams] = useState({
        domain: domain._id,
        pageIncrement: 0
    });
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);

    const permissions = useHasPermission({
        domain: id,
        environment: null,
        deployment: null,
        type: "domain"
    });

    const [showSettings, setShowSettings] = useState(false);
    const [settingsSetId, setSettingsSetId] = useState("");
    const [requiresLicense, setRequiresLicense] = useState(false);

    const getDomain = useCallback(async (id) => {
        const response = await Domain.getDomain(id);
        if (!response.error) {
            setDomain(response.data);
            setRequiresLicense(response.data.requiresLicense);
            setAssignedProducts(response.data.products);
            setSettingsSetId(response.data.settingsSet);
            setShowSaveButton(false);
        }
    }, [setDomain, setAssignedProducts]);

    const getAllProducts = useCallback(async () => {
        const response = await Domain.getAllProductsByDomain(Utils.getDomain());
        if (!response.error) {
            setProducts(response.data);
        }
    }, [setProducts]);

    const getAllDeploymentsByProductForDomain = useCallback(async (id) => {
        const response = await Product.getAllDeploymentsByProductForDomain(id);
        if (!response.error) {
            const returnObj = {};
            response.data.forEach(product => {
                returnObj[product._id] = product.deployments.length;
            });
            setAssignedDeploymentsToProduct(returnObj);
        }
    }, [setAssignedDeploymentsToProduct]);

    const getAllDeployments = useCallback(async (params) => {
        setDeployments([]);
        const response = await Deployment.getAllDeploymentsInDomain(
            params.domain,
            null,
            params.firstCursor,
            params.lastCursor,
            params.pageIncrement < 0,
            params.getLatest);

        if (response && !response.error) {
            setDeployments(response.data);
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });
        }

        setDeploymentParams({});
    }, [setDeployments]);

    useEffect(() => {
        if (!domain.rootDomain) {
            getAllProducts();
        }
    }, [getAllProducts, domain.rootDomain]);

    useEffect(() => {
        if (!domain._id) {
            return;
        }
        if (!domain.rootDomain && deploymentParams.domain) {
            getAllDeployments({ ...deploymentParams, domain: domain._id });
        }
    }, [getAllDeployments, domain._id, domain.rootDomain, deploymentParams]);

    useEffect(() => {
        if (domain._id) {
            setDeploymentParams({
                domain: domain._id,
                pageIncrement: 0
            });
        }
        return () => setDeployments([]);
    }, [domain._id]);

    useEffect(() => {
        if (id) {
            getAllDeploymentsByProductForDomain(id);
        }
    }, [id, getAllDeploymentsByProductForDomain]);

    useEffect(() => {
        if (id) {
            getDomain(id);
        }
    }, [id, getDomain]);

    useEffect(() => {
        if (showSaveButton) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [showSaveButton]);

    const previous = useCallback(() => {
        setDeploymentParams({
            domain: domain._id,
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain._id, cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setDeploymentParams({
            domain: domain._id,
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain._id, cursors.firstCursor, cursors.lastCursor]);

    const update = useCallback(async () => {
        const response = await Domain.updateDomain(domain._id, {
            name: domain.name,
            helpUrl: domain.helpUrl,
            requiresLicense,
            products: assignedProducts,
        });
        if (!response.error) {
            setShowSaveButton(false);
            PubSub.publish("refresh-domain-list", { editedDomainId: domain._id, editedName: domain.name });
        }
    }, [domain, requiresLicense, setShowSaveButton, assignedProducts]);

    const updateSelectedProducts = useCallback(({ _id }) => {
        setAssignedProducts(products => {
            if (products.indexOf(_id) > -1) {
                products.splice(products.indexOf(_id), 1);
            } else {
                products.push(_id);
            }
            return products;
        });
    }, [setAssignedProducts]);

    const unlockDeployment = useCallback(async (deployment) => {
        const response = await Deployment.updateDeployment(deployment._id, {
            name: deployment.name,
            description: deployment.description,
            productVersion: deployment.productVersion,
            actionInProgress: ACTION_TYPES.AVAILABLE
        });

        if (!response.error) {
            setDeploymentParams({
                domain: domain._id,
                pageIncrement: 0,
            });
            setCurrentPage(1);
        }
    }, [setDeploymentParams, domain._id]);

    const onCnameUpdate = useCallback(() => {
        setDeploymentParams({
            domain: domain._id,
            pageIncrement: 0,
        });
        setCurrentPage(1);
        setShowChangeCname(false);
    }, [domain._id])

    const getDeploymentStatus = useCallback((deployment) => {
        if (!deployment) {
            return translate('OTM_STANDBY');
        }

        switch (deployment.actionInProgress) {
            case ACTION_TYPES.DEPLOYMENT_IN_PROGRESS:
                return translate('OTM_DEPLOYMENT_IN_PROGRESS');
            case ACTION_TYPES.DOWNLOAD_IN_PROGRESS:
                return translate('OTM_DOWNLOAD_IN_PROGRESS');
            case ACTION_TYPES.BUILD_ONPREM_IN_PROGRESS:
                return translate('OTM_BUILD_IN_PROGRESS');
            case ACTION_TYPES.DOWNLOAD_ONPREM_BUILD_IN_PROGRESS:
                return translate('OTM_DOWNLOAD_BUILD_IN_PROGRESS');
            default:
                return translate('OTM_STANDBY');
        }
    }, [translate])

    const renderProductList = useMemo(() => {
        return !domain.rootDomain && (
            <div className="mt-3">
                <label htmlFor="products" className="form-label h6">{translate("OTM_PRODUCTS")}</label>

                <div className="row mt-n2">
                    {products.map(product => (
                        <div className="col-12 col-lg-6 mt-2" key={product._id}>
                            <div className="form-check form-switch" id="products">
                                <Toggle
                                    id={product._id}
                                    onChange={() => updateSelectedProducts(product)}
                                    defaultState={domain.products && domain.products.indexOf(product._id) > -1}
                                    disabled={!permissions.update || assignedDeploymentsToProduct[product._id]}
                                />
                                <label className="form-check-label ms-2" htmlFor={product._id}>
                                    {product.name}
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [products, domain.products, domain.rootDomain, permissions, assignedDeploymentsToProduct, updateSelectedProducts, translate]);

    const renderDeploymentsList = useMemo(() => {
        return (
            <div className="card mt-3">
                <div className="card-header border-bottom">
                    <Toolbar
                        buttons={[
                            {
                                name: "ChevronLeft",
                                title: translate("OTM_PREVIOUS"),
                                onClick: previous,
                                disabled: currentPage === 1
                            },
                            {
                                name: "ChevronRight",
                                title: translate("OTM_NEXT"),
                                onClick: next,
                                disabled: !hasNextPage
                            },
                        ]}
                        heading={translate("OTM_DEPLOYMENTS")}
                    />
                </div>
                <div className="card-body p-0 d-flex flex-column ">
                    <div className="list-group list-group-flush">
                        {deployments.map(deployment => (
                            <div className="list-group-item" key={deployment._id} id="deployments">
                                <div className="d-lg-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <div className="">{deployment.name}</div>
                                        <div className="small">{`${deployment._id} - ${getDeploymentStatus(deployment)}`}</div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-end mt-2 mt-lg-0">
                                        <Button
                                            disabled={
                                                !getCrudPermissions({ domain: domain._id, environment: deployment.environment, deployment: deployment._id, type: "deployment" }).update
                                            }
                                            variant="secondary"
                                            onClick={() => { setShowChangeCname(true); setSelectedDeployment(deployment); }}
                                            className="me-2"
                                        >
                                            {translate("OTM_CNAME")}
                                        </Button>
                                        <Button
                                            disabled={
                                                !getCrudPermissions({ domain: domain._id, environment: deployment.environment, deployment: deployment._id, type: "deployment" }).update
                                                || deployment.actionInProgress === ACTION_TYPES.AVAILABLE
                                            }
                                            variant="secondary"
                                            onClick={() => unlockDeployment(deployment)}
                                        >
                                            {translate("OTM_UNLOCK")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {deployments.length === 0 && <div className="p-3">{translate("OTM_NO_DEPLOYMENTS")}</div>}
                    </div>
                </div>
            </div>
        );
    }, [translate, domain._id, deployments, unlockDeployment, getDeploymentStatus, currentPage, next, previous, hasNextPage])

    const renderToolbar = useMemo(() => (
        <Toolbar
            buttons={[
                {
                    name: "Save",
                    title: translate("OTM_SAVE"),
                    onClick: update,
                    disabled: !showSaveButton
                }
            ]}
            heading={translate('OTM_DOMAIN_DETAILS')}
        />
    ), [translate, update, showSaveButton]);

    const renderLicenseRequired = useMemo(() => domain.rootDomain ? null : (
        <div className="mt-3">
            <label htmlFor={domain.name} className="form-label h6">{translate("OTM_REQUIRES_LICENSE")}</label>
            <div className="form-check form-switch">
                <Toggle
                    defaultState={domain.requiresLicense}
                    onChange={() => setRequiresLicense((currentValue) => !currentValue)}
                />
            </div>
        </div>
    ), [domain.name, domain.rootDomain, domain.requiresLicense, translate]);

    const renderModals = useMemo(() => (
        <>
            {
                settingsSetId &&
                <SettingsModal
                    show={showSettings}
                    onCancel={() => setShowSettings(false)}
                    settingsSetId={settingsSetId}
                    permissions={permissions}
                    context={{ type: 'domain', objectId: domain._id, headerText: domain.name }} />
            }
            {
                selectedDeployment &&
                <ChangeCnameModal
                    show={showChangeCname}
                    deployment={selectedDeployment}
                    onCancel={() => setShowChangeCname(false)}
                    onUpdate={onCnameUpdate} />
            }
        </>
    ), [domain, permissions, selectedDeployment, settingsSetId, showChangeCname, showSettings, onCnameUpdate]);

    const renderDomainDetails = useMemo(() => (
        <div className="card mt-3">
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body">
                <Formik initialValues={{
                    name: domain.name || "",
                    helpUrl: domain.helpUrl || ""
                }} enableReinitialize>
                    <Form onChange={(event) => {
                        setDomain({ ...domain, [event.target.name]: event.target.value });
                        setShowSaveButton(true);
                    }}>
                        <Button
                            disabled={!permissions.read}
                            variant="primary"
                            className="text-white"
                            onClick={() => setShowSettings(true)}
                        >
                            {permissions.update ? translate("OTM_EDIT_SETTINGS") : translate("OTM_VIEW_SETTINGS")}
                        </Button>
                        <TextBox label={translate("OTM_NAME")} type="text" name="name" css="mt-3" />
                        <TextBox label={translate("OTM_HELP_URL")} type="text" name="helpUrl" css="mt-3" />
                        {renderLicenseRequired}
                        {renderProductList}
                    </Form>
                </Formik>
            </div>
        </div>
    ), [domain, permissions, renderLicenseRequired, renderProductList, renderToolbar, translate]);

    return (
        <div style={{ overflowX: 'hidden', overflowY: 'auto' }}>
            {renderModals}
            {renderDomainDetails}
            {!domain.rootDomain && renderDeploymentsList}
        </div>
    );
};

export default RootDomainDetails;
